const COOKIE_NAME = 'lf_session'
const TOKEN_KEY = 'jwtToken'
const MAX_AGE = 3 * 24 * 60 * 60 // seconds, matching the JWT's 3d expiry
const PARENT_DOMAIN = 'loadfetcher.com'

// Records that this browser has held a session cookie. Without it a missing
// cookie is ambiguous: it could mean a sign-out on the other host, or a session
// that simply predates the cookie. Getting that wrong signs everyone out.
const ADOPTED_KEY = 'lf_session_adopted'

// Storage and cookies both throw rather than read empty when the browser blocks
// them: Chrome with all cookies blocked, some privacy extensions, a sandboxed
// frame. This module runs before React mounts, so an unguarded throw here is a
// blank page rather than a degraded one.
function readStored(key) {
	try {
		return localStorage.getItem(key)
	} catch (err) {
		return null
	}
}

function writeStored(key, value) {
	try {
		localStorage.setItem(key, value)
	} catch (err) {}
}

function removeStored(key) {
	try {
		localStorage.removeItem(key)
	} catch (err) {}
}

function readCookieHeader() {
	try {
		return document.cookie
	} catch (err) {
		return ''
	}
}

function writeCookieHeader(value) {
	try {
		document.cookie = value
	} catch (err) {}
}

// Mirrors cookieDomainFor on the server: a Domain attribute is only valid on
// hosts under that domain, so localhost and the *.ondigitalocean.app hostname
// get a host-only cookie and no cross-host carry.
function domainAttribute() {
	const host = (window.location.hostname || '').toLowerCase()

	if (host === PARENT_DOMAIN || host.endsWith(`.${PARENT_DOMAIN}`)) {
		return `; domain=.${PARENT_DOMAIN}`
	}

	return ''
}

function readSessionCookie() {
	const match = readCookieHeader().match(
		new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]*)`)
	)

	return match ? decodeURIComponent(match[1]) : null
}

function writeSessionCookie(token) {
	const secure = window.location.protocol === 'https:' ? '; secure' : ''

	writeCookieHeader(
		`${COOKIE_NAME}=${encodeURIComponent(token)}` +
			`; max-age=${MAX_AGE}; path=/; samesite=lax${secure}${domainAttribute()}`
	)
}

// Expire both the domain-scoped cookie and any host-only one, so neither can
// outlive a sign-out and put the user back in on the next load.
function expireSessionCookie() {
	const expired = `${COOKIE_NAME}=; max-age=0; path=/`
	const domain = domainAttribute()

	writeCookieHeader(expired)
	if (domain) writeCookieHeader(expired + domain)
}

// The cookie is the source of truth. localStorage stays the read path for the
// rest of the app, so this runs once before render and leaves the two in
// agreement.
export function syncSessionWithCookie() {
	const cookieToken = readSessionCookie()
	const storedToken = readStored(TOKEN_KEY)

	if (cookieToken) {
		if (cookieToken !== storedToken) writeStored(TOKEN_KEY, cookieToken)
		writeStored(ADOPTED_KEY, '1')
		return
	}

	if (!storedToken) return

	// A cookie has existed here before, so its absence is a sign-out somewhere
	// else on the domain rather than a session older than this mechanism.
	if (readStored(ADOPTED_KEY)) {
		removeStored(TOKEN_KEY)
		return
	}

	// Anyone signed in before this shipped has a token and no cookie. Promote it
	// rather than treating it as a sign-out, which would log out every existing
	// user the moment this deploys.
	writeSessionCookie(storedToken)
	writeStored(ADOPTED_KEY, '1')
}

// Every path that signs a user in goes through here, so no login route can
// leave a token in storage with no cookie beside it. The server writes the same
// cookie on its own responses; the two always agree on the value.
export function startSession(token) {
	if (!token) return

	writeStored(TOKEN_KEY, token)
	writeStored(ADOPTED_KEY, '1')
	writeSessionCookie(token)
}

export function clearSession() {
	removeStored(TOKEN_KEY)
	expireSessionCookie()
}
